import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Tag } from '@/types/crm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Tags } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';

interface TagManagerProps {
  businessId: string;
}

const TagManager: React.FC<TagManagerProps> = ({ businessId }) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);

  // Form state
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('#3b82f6');
  const [tagDescription, setTagDescription] = useState('');

  useEffect(() => {
    fetchTags();
  }, [businessId]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tags')
        .select(`
          *,
          contact_tags (count)
        `)
        .eq('business_id', businessId)
        .order('name');

      if (error) throw error;

      // Calculate contact counts
      const tagsWithCounts = (data || []).map((tag: any) => ({
        ...tag,
        contact_count: tag.contact_tags?.[0]?.count || 0,
      }));

      setTags(tagsWithCounts);
    } catch (error: any) {
      console.error('Error fetching tags:', error);
      toast.error('Failed to load tags', {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!tagName.trim()) {
      toast.error('Please enter a tag name');
      return;
    }

    try {
      const { error } = await supabase.from('tags').insert({
        business_id: businessId,
        name: tagName.toLowerCase().trim(),
        color: tagColor,
        description: tagDescription.trim() || null,
      });

      if (error) throw error;

      toast.success('Tag created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchTags();
    } catch (error: any) {
      console.error('Error creating tag:', error);
      toast.error('Failed to create tag', {
        description: error.message,
      });
    }
  };

  const handleDeleteTag = async () => {
    if (!selectedTag) return;

    try {
      const { error } = await supabase.from('tags').delete().eq('id', selectedTag.id);

      if (error) throw error;

      toast.success('Tag deleted successfully');
      setShowDeleteDialog(false);
      setSelectedTag(null);
      fetchTags();
    } catch (error: any) {
      console.error('Error deleting tag:', error);
      toast.error('Failed to delete tag', {
        description: error.message,
      });
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openDeleteDialog = (tag: Tag) => {
    setSelectedTag(tag);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setTagName('');
    setTagColor('#3b82f6');
    setTagDescription('');
  };

  const predefinedColors = [
    '#3b82f6', // blue
    '#8b5cf6', // purple
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#06b6d4', // cyan
    '#ec4899', // pink
    '#84cc16', // lime
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Tag Management</CardTitle>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Create Tag
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Tags help you organize and categorize your contacts. Create custom tags to segment your
            audience.
          </p>
        </CardContent>
      </Card>

      {/* Tags Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">
            Loading tags...
          </div>
        ) : tags.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Tags className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No tags yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first tag to start organizing contacts
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Create Tag
              </Button>
            </CardContent>
          </Card>
        ) : (
          tags.map((tag) => (
            <Card key={tag.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <Badge
                    variant="outline"
                    style={{
                      borderColor: tag.color,
                      backgroundColor: `${tag.color}15`,
                      color: tag.color,
                    }}
                    className="text-sm font-medium"
                  >
                    {tag.name}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDeleteDialog(tag)}
                    className="h-8 w-8 p-0"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {tag.description && (
                    <p className="text-sm text-muted-foreground">{tag.description}</p>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Contacts:</span>
                    <span className="font-medium">{tag.contact_count || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Tag Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tag</DialogTitle>
            <DialogDescription>
              Add a new tag to organize your contacts. Choose a name and color.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tag-name">Tag Name</Label>
              <Input
                id="tag-name"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                placeholder="e.g., vip, high-value, new-customer"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tag-description">Description (Optional)</Label>
              <Input
                id="tag-description"
                value={tagDescription}
                onChange={(e) => setTagDescription(e.target.value)}
                placeholder="Brief description..."
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2 flex-wrap">
                {predefinedColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      tagColor === color ? 'border-foreground scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setTagColor(color)}
                  />
                ))}
              </div>
            </div>
            <div className="pt-2">
              <Label className="text-sm text-muted-foreground">Preview</Label>
              <div className="mt-2">
                <Badge
                  variant="outline"
                  style={{
                    borderColor: tagColor,
                    backgroundColor: `${tagColor}15`,
                    color: tagColor,
                  }}
                >
                  {tagName || 'Tag Name'}
                </Badge>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTag}>Create Tag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tag?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the tag "{selectedTag?.name}"? This will remove it
              from all {selectedTag?.contact_count || 0} contact(s). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedTag(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTag} className="bg-destructive hover:bg-destructive/90">
              Delete Tag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TagManager;
